import { Form, Input, Modal, Select, Upload, UploadFile, Button as AntButton, message, Image } from "antd";
import Draggable from "react-draggable";
import {
  useCreateWorkOrderMutation,
  useGetWorkOrderCalculatedFieldsMutation,
  useAddWorkLogMediaMutation
} from "../../../store/services/api/work-order-api";
import {
  WorkOrderForm,
  workOrderPriorityOptions,
  WorkOrderType
} from "../../../store/data/entities/work-order";
import React, { useRef, useEffect, useState } from "react";
import UserSelect from "../../../components/forms/controls/UserSelect";
import LuxonDatePicker from "../../../components/form-controls/LuxonDatePicker";
import { DateTime } from "luxon";
import { LoadingOutlined, CalendarOutlined, UploadOutlined, CameraOutlined } from "@ant-design/icons";
import { useCamera } from "../../../screens/technician-v2/hooks/useCamera";
import { base64toFile } from "../../../utils/imageUtils";
import useUser from "../../../hooks/useUser";
import { set } from "react-hook-form";
import { useGetAllFallOutsQuery } from "../../../store/services/api/fall-out-api";

interface FollowUpWorkOrderModal {
  visible: boolean;
  onClose: () => void;
  onSuccess: (workOrderId: string) => void;
  assetLabel: string;
  assetId: string;
  parentWorkOrderId: string | undefined;
  isDraggable?: boolean;
}

export interface RcFile extends File {
  uid: string;
  lastModifiedDate: Date;
}

export default function FollowUpWorkOrderModal(props: FollowUpWorkOrderModal) {
  const [createWorkOrder, { isLoading: createLoading }] = useCreateWorkOrderMutation();
  const [uploadWorkLogMedia, { isLoading }] = useAddWorkLogMediaMutation();
  const [getCalculatedFields, { isLoading: fieldsLoading }] =
    useGetWorkOrderCalculatedFieldsMutation();
  const { showCamera } = useCamera();
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [form] = Form.useForm<WorkOrderForm>();
  const startDate = Form.useWatch("scheduledStartDate", form);
  const priority = Form.useWatch("priority", form);
  const [fileList, setFileList] = useState<any[]>([]);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const { user } = useUser();
  const {data: fallOuts} = useGetAllFallOutsQuery();

  useEffect(() => {
    form.resetFields();
    setFileList([]);
    setImagePreview(null);
  }, [props.visible]);

  const handleOnSubmit = async (data: WorkOrderForm) => {
    if (!props.assetId || !props.parentWorkOrderId) return;

    try {
      let imageBase64 = null;
      if (fileList.length > 0 && fileList[0].originFileObj) {
        imageBase64 = await toBase64(fileList[0].originFileObj);
      }

      const result = await createWorkOrder({
        ...data,
        type: WorkOrderType.Corrective,
        assets: [props.assetId],
        parentWorkOrderId: props.parentWorkOrderId,
        scheduledFinishDate: data.scheduledFinishDate.toISO(),
        scheduledStartDate: data.scheduledStartDate.toISO(),
      }).unwrap();
      if (imageBase64 && user) {
        const file = base64toFile(imageBase64, `work-order-image-${Date.now()}.jpg`);
        const uploadFile = mapFileToUploadFile(file);
        const submissionFile = uploadFileToFile(uploadFile);
        
        await uploadWorkLogMedia({
          WorkLogFileDto: {
            workOrderId: result.id,
            workLogEntryId: undefined,
            files: [submissionFile],
            fileURIsToRemove: [],
          },
          NewWorkOrderLogDto: {
            userId: user.id,
            comments: "Asset required follow up Work Order",
            id: "",
            fileURIs: [],
            user: "",
            previousComments: undefined,
            createdOn: undefined,
            createdById: user.id
          },
        }).unwrap();
      }

      if (props.onSuccess) {
        props.onSuccess(result.id);
      }

    } catch (error) {
      console.error("Error creating work order:", error);
      message.error("Failed to create work order");
    } finally {
      props.onClose();
    }
  };

  const uploadFileToFile = (uploadFile: UploadFile): File => {
    if (uploadFile.originFileObj instanceof File) {
      return uploadFile.originFileObj;
    }
    throw new Error("Invalid file object detected");
  };

  const mapFallOutOptions = (fallOuts: any) =>
    (fallOuts ?? []).map(item => ({
      label: item.fallOutCode,
      value: item.id
    }));

  useEffect(() => {
    if (priority == undefined) return;

    getCalculatedFields({
      scheduledStartDate: startDate?.toUTC().toISO(),
      priority: priority,
      assets: [props.assetId],
      timezoneId: DateTime.local().zoneName
    })
      .unwrap()
      .then((res) => {
        const finishDate: DateTime = DateTime.fromISO(res.scheduledFinishDate);
        if (finishDate.isValid) {
          form.setFieldValue("scheduledFinishDate", finishDate);
        }
      });
  }, [startDate, props.assetId, priority]);

  const toBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });

  const convertToRcFile = (file: File): RcFile => {
    const rcFile = new File([file], file.name, { type: file.type }) as RcFile;
    rcFile.uid = Date.now().toString();
    return rcFile;
  };

  const mapFileToUploadFile = (file: File): any => ({
    uid: Date.now().toString(),
    name: file.name,
    status: "done",
    originFileObj: convertToRcFile(file),
  });

  const handleCameraButtonClick = () => {
    const handleOnResult = (result: string) => {
      const file = base64toFile(result, `work-order-image.jpg`);
      if (!(file instanceof File)) {
        return;
      }

      const uploadFile = mapFileToUploadFile(file);
      setFileList([uploadFile]);
      setImagePreview(result); // Set the base64 string for preview
    };

    showCamera({
      mode: "photo",
      onResult: handleOnResult,
    });
  };

  const handleFileSelect = (file: File) => {
    const uploadFile = mapFileToUploadFile(file);
    setFileList([uploadFile]);

    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveFile = () => {
    setFileList([]);
    setImagePreview(null);
  };

  const uploadProps = {
    onRemove: handleRemoveFile,
    beforeUpload: (file: File) => {
      handleFileSelect(file);
      return false; 
    },
    fileList,
    maxCount: 1,
    accept: "image/*",
    showUploadList: false 
  };


  
  const dragData = useRef<{
    startX: number;
    startY: number;
    initialX: number;
    initialY: number;
  } | null>(null);

  const [disabled, setDisabled] = useState(true);
  const draggleRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
    const resetToCenter = () => {
    setPosition({ x: 0, y: 0 });
  };

  return (
    
      <Modal
      getContainer={() => document.body} 
      title={
        <div
            style={{ cursor: "move", width: "100%" }}
            onMouseOver={() => (props.isDraggable) && disabled && setDisabled(false)}
            onMouseOut={() => setDisabled(true)}
          >
            Follow-up Work Order
          </div>
    }
      open={props.visible}
      onOk={() => form.submit()}
      onCancel={() => {
      props.onClose();
    }}
      confirmLoading={createLoading}
      destroyOnClose
      centered={!props.isDraggable}
      width={Math.min(600, window.innerWidth - 24)}
      style={
        props.isDraggable
          ? {
              top: position.y,
              left: position.x + window.innerWidth / 2 - 300,
              margin: 0,
              transform: "none",
              position: "absolute",
              transition: "none",
            }
          : undefined
      }
      modalRender={(modal) =>
        props.isDraggable ? (
          <Draggable
            disabled={disabled}
            nodeRef={draggleRef}
            onStart={() => setIsDragging(true)}
            onStop={() => setIsDragging(false)}
          >
            <div ref={draggleRef}>{modal}</div>
          </Draggable>
        ) : (
          modal
        )
      }
      >
        
      <Form
        style={{ maxHeight: "80vh", overflowY: "auto" }}
        form={form}
        onFinish={handleOnSubmit}
        disabled={createLoading}
        layout="vertical"
      >
        <Form.Item label="Asset">
          <Input value={props?.assetLabel} disabled />
        </Form.Item>

        <Form.Item
          name="description"
          label="Description"
          rules={[{ required: true }]}
        >
          <Input.TextArea rows={3} />
        </Form.Item>

        <Form.Item
          name="fallOutId"
          label="Fault Code"
          rules={[{ required: false }]}
          style={{ flex: 1 }}
        >
          <Select
            showSearch
            allowClear
            optionFilterProp="label"
            filterOption={(input, option) =>
              String(option?.label ?? '')
                .toLowerCase()
                .includes(input.toLowerCase())
            }
            options={mapFallOutOptions(fallOuts)}
          />
        </Form.Item>

        <Form.Item
          name="technicianId"
          label="Technician"
          rules={[{ required: true }]}
        >
          <UserSelect userRoles={["Technician"]} style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item
          name="buildingLeadId"
          label="Building Lead"
          rules={[{ required: true }]}
        >
          <UserSelect userRoles={["BuildingLead"]} style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item
          name="priority"
          label="Priority"
          rules={[{ required: true }]}
        >
          <Select options={workOrderPriorityOptions} />
        </Form.Item>

        <Form.Item
          name="scheduledStartDate"
          label="Created On"
          initialValue={DateTime.now()}
        >
          <LuxonDatePicker disabled />
        </Form.Item>

        <Form.Item name="scheduledFinishDate" label="Must Finish By">
          <LuxonDatePicker
            suffixIcon={fieldsLoading ? <LoadingOutlined /> : <CalendarOutlined />}
            disabled
          />
        </Form.Item>

        <Form.Item label="Upload Image">
          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
            <Upload {...uploadProps}>
              <AntButton icon={<UploadOutlined />}>
                Select Image
              </AntButton>
            </Upload>
            <AntButton
              icon={<CameraOutlined />}
              onClick={handleCameraButtonClick}
            >
              Take Photo
            </AntButton>
          </div>
          
          {imagePreview && (
            <div style={{ marginTop: '16px' }}>
              <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
                Image Preview:
              </div>
              <Image
                src={imagePreview}
                alt="Work order image preview"
                style={{ 
                  maxWidth: '100%', 
                  maxHeight: '200px', 
                  borderRadius: '8px',
                  border: '1px solid #d9d9d9'
                }}
                preview={{
                  mask: null,
                }}
              />
              <div style={{ marginTop: '8px' }}>
                <AntButton 
                  type="link" 
                  danger 
                  onClick={handleRemoveFile}
                  size="small"
                >
                  Remove Image
                </AntButton>
              </div>
            </div>
          )}

          <div style={{ fontSize: '12px', color: '#999', marginTop: '8px' }}>
            {fileList.length > 0 ? `Selected: ${fileList[0].name}` : 'No image selected'}
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
}